import {
  ApolloClient,
  ApolloLink,
  fromPromise,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";

import { REFRESH_TOKEN, READ_IAM_QUERY } from "graphqlQueries/auth";
import { refreshAuthMapper } from "graphqlQueries/auth.map";

import { getQueryName } from "utils/getQueryName";
import { gqlCacheTypePolicies } from "utils/gqlCacheTypePolicies";
import {
  getRefreshTokenFromStorage,
  getTokenFromStorage,
  setAuthInfoToStorage,
  shouldRefreshToken,
  clearAuthInfoFromLocalStorage,
} from "utils/jwtUtils";

let isRefreshing = false;
let pendingRequests = [];

const OPERATION_WITHOUT_TOKEN = [
  getQueryName(REFRESH_TOKEN),
  getQueryName(READ_IAM_QUERY),
];

const uploadLink = createUploadLink();

const authLink = new ApolloLink((operation, forward) => {
  if (
    !OPERATION_WITHOUT_TOKEN.includes(operation.operationName) &&
    shouldRefreshToken()
  ) {
    return fetchTokenLink(operation, forward);
  }

  setTokenToOperation(operation);

  return forward(operation);
});

const errorLink = onError(({ graphQLErrors }) => {
  if (!graphQLErrors) {
    return;
  }
  for (let { message } of graphQLErrors) {
    if (message === "Unauthorized" || message === "Signature has expired") {
      clearAuthInfoFromLocalStorage();
      return;
    }
  }
});

const fetchTokenLink = (operation, forward) => {
  if (!isRefreshing) {
    isRefreshing = true;
    return fromPromise(refreshToken()).flatMap((isTokenRefreshed) => {
      if (!isTokenRefreshed) {
        pendingRequests = [];
        clearAuthInfoFromLocalStorage();
      }
      resolvePendingRequests();
      isRefreshing = false;
      setTokenToOperation(operation);
      return forward(operation);
    });
  } else {
    return fromPromise(
      new Promise((resolve) => {
        pendingRequests.push(() => resolve());
      }),
    ).flatMap(() => {
      setTokenToOperation(operation);
      return forward(operation);
    });
  }
};

const setTokenToOperation = (operation, token = null) => {
  if (!token) {
    token = getTokenFromStorage();
  }
  operation.setContext(({ headers }) => {
    return {
      headers: {
        ...headers,
        ...(token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {}),
      },
    };
  });
};

const resolvePendingRequests = () => {
  pendingRequests.map((callback) => callback());
  pendingRequests = [];
};

const refreshToken = async () => {
  const refreshToken = getRefreshTokenFromStorage();

  if (!refreshToken) {
    return false;
  }

  const { data, errors } = await graphqlClient.mutate({
    mutation: REFRESH_TOKEN,
    variables: { refresh_token: refreshToken },
  });

  if (errors?.length) {
    return false;
  }
  const newAuthData = refreshAuthMapper(data);
  setAuthInfoToStorage(newAuthData);
  return true;
};

export const graphqlClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(uploadLink)]),
  connectToDevTools: false,
  cache: new InMemoryCache({
    typePolicies: gqlCacheTypePolicies,
  }),
  defaultOptions: {
    mutate: { errorPolicy: "all" },
    query: { errorPolicy: "all" },
  },
});
